import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { DollarSign, Save, Download, Upload, Plus, Trash2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const MONEY = [
  ["cash_on_hand", "Cash on hand"],
  ["mrr", "MRR / recurring revenue"],
  ["other_monthly_revenue", "Other monthly revenue"],
  ["fixed_monthly_cost", "Fixed monthly cost"],
  ["variable_monthly_cost", "Variable monthly cost"],
];

const METRIC_LABELS = {
  gross_monthly_burn: "Gross burn / mo",
  net_monthly_burn: "Net burn / mo",
  total_monthly_revenue: "Revenue / mo",
  runway_months: "Runway (months)",
  cash_due_in_30d: "Cash in (30d)",
  cash_due_out_30d: "Cash out (30d)",
};

function Metric({ label, value, cannot, testid }) {
  const shown = typeof value === "number"
    ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : value;
  return (
    <div className="bg-wr-surface p-3" data-testid={testid}>
      {value != null ? (
        <p className="wr-mono text-base text-white">{shown}</p>
      ) : (
        <p className="wr-mono text-xs text-amber-400" data-testid={`${testid}-cannot`}>cannot calculate</p>
      )}
      <p className="wr-label !text-[0.55rem] mt-1">{label}</p>
      {value == null && cannot && <p className="text-[0.6rem] text-wr-muted mt-1">{cannot}</p>}
    </div>
  );
}

export default function Financial() {
  const [inputs, setInputs] = useState({ receivables: [], payables: [] });
  const [metrics, setMetrics] = useState({});
  const [flags, setFlags] = useState({});
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/financial/pulse");
      setInputs({ receivables: [], payables: [], ...data.inputs });
      setMetrics(data.metrics || {});
      setFlags(data.flags || {});
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/financial/pulse", inputs);
      setInputs({ receivables: [], payables: [], ...data.inputs });
      setMetrics(data.metrics || {});
      setFlags(data.flags || {});
      toast.success("Financial pulse saved.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const downloadTemplate = async (format) => {
    try {
      const res = await api.get(`/financial/template?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `financial_pulse_template.${format}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/financial/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (err) { toast.error(apiError(err)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const confirmImport = async () => {
    if (!preview || preview.has_errors) return;
    setBusy(true);
    try {
      const merged = { ...inputs, ...preview.proposed_inputs };
      const { data } = await api.post("/financial/import/confirm", merged);
      setInputs({ receivables: [], payables: [], ...data.inputs });
      setMetrics(data.metrics || {});
      setFlags(data.flags || {});
      setPreview(null);
      toast.success("Import applied.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const addItem = (key) => setInputs({ ...inputs, [key]: [...(inputs[key] || []), { amount: "", due_date: "", status: "open" }] });
  const updItem = (key, i, field, val) => {
    const arr = [...(inputs[key] || [])];
    arr[i] = { ...arr[i], [field]: val };
    setInputs({ ...inputs, [key]: arr });
  };
  const delItem = (key, i) => setInputs({ ...inputs, [key]: (inputs[key] || []).filter((_, idx) => idx !== i) });

  const cannot = flags.cannot_calculate || {};

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-6 sm:py-8 pb-28" data-testid="financial-page">
      <p className="wr-label !text-wr-red">▸ SARGE · FINANCIAL PULSE</p>
      <h1 className="wr-heading font-bold text-white text-2xl sm:text-3xl mt-2 flex items-center gap-2"><DollarSign size={24} /> Cash &amp; Runway.</h1>
      <p className="text-sm text-wr-sub mt-2">Sarge never guesses money. Enter figures here; all metrics are computed deterministically and shown with their as-of date.</p>

      {flags.stale && (
        <div className="mt-4 border border-amber-500/30 bg-amber-500/5 p-3 flex items-center gap-2" data-testid="financial-stale">
          <AlertTriangle size={14} className="text-amber-400" />
          <p className="text-xs text-amber-300/90">This data is stale (as-of {flags.as_of}). Update it so Sarge's advice stays grounded.</p>
        </div>
      )}

      {/* Metrics */}
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-px bg-wr-border border border-wr-border" data-testid="financial-metrics">
        {Object.entries(METRIC_LABELS).map(([k, label]) => (
          <Metric key={k} label={label} value={metrics[k]} cannot={cannot[k]} testid={`fin-metric-${k}`} />
        ))}
      </div>

      {/* Inputs */}
      <h2 className="wr-label mt-8 mb-3">Inputs</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {MONEY.map(([k, label]) => (
          <div key={k}>
            <label className="wr-label">{label}</label>
            <Input data-testid={`fin-input-${k}`} value={inputs[k] ?? ""} onChange={(e) => setInputs({ ...inputs, [k]: e.target.value })} inputMode="decimal" className="mt-1 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0" />
          </div>
        ))}
        <div>
          <label className="wr-label">As-of date (YYYY-MM-DD)</label>
          <Input data-testid="fin-input-as_of" value={inputs.as_of ?? ""} onChange={(e) => setInputs({ ...inputs, as_of: e.target.value })} className="mt-1 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0" />
        </div>
        <div>
          <label className="wr-label">Source</label>
          <Input data-testid="fin-input-source" value={inputs.source ?? ""} onChange={(e) => setInputs({ ...inputs, source: e.target.value })} className="mt-1 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0" />
        </div>
      </div>

      {/* Receivables / payables */}
      {[["receivables", "Receivables (money in)"], ["payables", "Payables (money out)"]].map(([key, label]) => (
        <div key={key} className="mt-6" data-testid={`fin-${key}`}>
          <div className="flex items-center justify-between">
            <h2 className="wr-label">{label}</h2>
            <button onClick={() => addItem(key)} data-testid={`fin-add-${key}`} className="wr-label hover:text-white flex items-center gap-1"><Plus size={12} /> Add</button>
          </div>
          <div className="mt-2 space-y-2">
            {(inputs[key] || []).map((it, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`fin-${key}-row-${i}`}>
                <Input placeholder="amount" value={it.amount ?? ""} onChange={(e) => updItem(key, i, "amount", e.target.value)} className="bg-wr-bg border-wr-border rounded-none focus-visible:ring-0 h-9" />
                <Input placeholder="due YYYY-MM-DD" value={it.due_date ?? ""} onChange={(e) => updItem(key, i, "due_date", e.target.value)} className="bg-wr-bg border-wr-border rounded-none focus-visible:ring-0 h-9" />
                <button onClick={() => delItem(key, i)} className="text-wr-sub hover:text-wr-red shrink-0"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="mt-6">
        <Button onClick={save} disabled={busy} data-testid="financial-save" className="rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider h-11 px-6">
          <Save size={15} className="mr-2" /> {busy ? "Saving…" : "Save financials"}
        </Button>
      </div>

      {/* Import / template */}
      <div className="mt-8 border-t border-wr-border pt-6" data-testid="financial-import">
        <h2 className="wr-label">Template &amp; import</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button onClick={() => downloadTemplate("csv")} variant="outline" data-testid="fin-template-csv" className="rounded-none border-wr-border bg-transparent text-wr-sub hover:text-white wr-mono uppercase text-xs h-9"><Download size={13} className="mr-1.5" /> CSV template</Button>
          <Button onClick={() => downloadTemplate("xlsx")} variant="outline" data-testid="fin-template-xlsx" className="rounded-none border-wr-border bg-transparent text-wr-sub hover:text-white wr-mono uppercase text-xs h-9"><Download size={13} className="mr-1.5" /> XLSX template</Button>
          <Button onClick={() => fileRef.current?.click()} variant="outline" data-testid="fin-import-open" className="rounded-none border-wr-border bg-transparent text-wr-sub hover:text-white wr-mono uppercase text-xs h-9"><Upload size={13} className="mr-1.5" /> Import file</Button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx" onChange={onFile} className="hidden" data-testid="fin-import-file" />
        </div>

        {preview && (
          <div className="mt-4 border border-wr-border bg-wr-surface p-4" data-testid="fin-import-preview">
            <p className="wr-label">Review mapped changes before applying</p>
            <div className="mt-2 space-y-1">
              {preview.mapped.map((m) => (
                <div key={m.field} className="flex items-center gap-3 text-xs border-b border-wr-border py-1.5" data-testid={`fin-preview-${m.field}`}>
                  <span className="text-white w-40">{m.label}</span>
                  <span className="text-wr-muted">{m.current ?? "—"}</span>
                  <span className="text-wr-sub">→</span>
                  <span className={m.error ? "text-wr-red" : "text-emerald-400"}>{m.new}</span>
                  {m.error && <span className="text-wr-red ml-auto">{m.error}</span>}
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Button onClick={confirmImport} disabled={busy || preview.has_errors} data-testid="fin-import-confirm" className="rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase text-xs h-9 disabled:opacity-50">Apply import</Button>
              <Button onClick={() => setPreview(null)} variant="outline" data-testid="fin-import-cancel" className="rounded-none border-wr-border bg-transparent text-wr-sub wr-mono uppercase text-xs h-9">Cancel</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
